/**
 * ClientsPage — Inline-formulär för att skapa och redigera kunder.
 *
 * Mönster: card under header (som Verksamheter-sidan).
 * Åtkomst: ENDAST super_admin (Kunder är centralt administrerat objekt).
 *
 * Source-of-Truth:
 * - Kunder är plattformsnivå-objekt, hanteras enbart av super_admin
 * - customer_admin och employee har ingen åtkomst till denna sida
 * - Inline-formulär visas bara för super_admin
 */
import React, { useEffect, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Building2, Mail, Phone, Trash2, Edit, Plus, X } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

// ── Constants ──────────────────────────────────────────────────────────────────

const VERKSAMHETSUPPDRAG_OPTIONS = [
  'Stöd och matchning',
  'Sysselsättning',
  'Arbetsträning',
  'Arbetsprövning',
  'Lönebidrag',
  'Utveckling och fördjupning',
  'Arbetsmarknadsutbildning',
  'Jobbcoachning',
];

const KATEGORI_TYPER = [
  { value: 'uppdragsgivare', label: 'Uppdragsgivare' },
  { value: 'uppdragstagare', label: 'Uppdragstagare' },
];

const STATUS_OPTIONS = [
  { value: 'aktiv',    label: 'Aktiv' },
  { value: 'inaktiv',  label: 'Inaktiv' },
  { value: 'pausad',   label: 'Pausad' },
];

const selectCls = (err) =>
  `h-10 w-full px-3 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring ${
    err ? 'border-destructive focus:ring-destructive' : 'border-input'
  }`;

// ── Form state builders ────────────────────────────────────────────────────────

const buildEmpty = () => ({
  namn:               '',
  org_nummer:         '',
  adress:             { gata: '', postnummer: '', ort: '' },
  kontaktperson:      '',
  epost:              '',
  telefon:            '',
  kollektivavtal:     '',
  antal_anvandare:    1,
  fakturaunderlag:    '',
  betalning:          { bankgiro: '', plusgiro: '', bankkonto: '' },
  kategori:           { typ: 'uppdragsgivare', underkategori: '' },
  verksamhetsuppdrag: [],
  status:             'aktiv',
});

const fromClient = (c) => ({
  namn:               c.namn               || '',
  org_nummer:         c.org_nummer         || '',
  adress:             c.adress             || { gata: '', postnummer: '', ort: '' },
  kontaktperson:      c.kontaktperson      || '',
  epost:              c.epost              || '',
  telefon:            c.telefon            || '',
  kollektivavtal:     c.kollektivavtal     || '',
  antal_anvandare:    c.antal_anvandare    || 1,
  fakturaunderlag:    c.fakturaunderlag    || '',
  betalning:          c.betalning          || { bankgiro: '', plusgiro: '', bankkonto: '' },
  kategori:           c.kategori           || { typ: 'uppdragsgivare', underkategori: '' },
  verksamhetsuppdrag: c.verksamhetsuppdrag || [],
  status:             c.status             || 'aktiv',
});

// ── ClientForm ─────────────────────────────────────────────────────────────────

const ClientForm = ({ initial, onSave, onCancel, isEdit, clientId }) => {
  const { user }                  = useAuth();
  const [form, setForm]           = useState(initial);
  const [errors, setErrors]       = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSub]      = useState(false);

  const set = (field, value) =>
    setForm(f => ({ ...f, [field]: value }));

  const setNested = (parent, field, value) =>
    setForm(f => ({ ...f, [parent]: { ...f[parent], [field]: value } }));

  const toggleUppdrag = (value) =>
    setForm(f => ({
      ...f,
      verksamhetsuppdrag: f.verksamhetsuppdrag.includes(value)
        ? f.verksamhetsuppdrag.filter(v => v !== value)
        : [...f.verksamhetsuppdrag, value],
    }));

  const validate = (f) => {
    const e = {};
    if (!f.namn.trim())               e.namn          = 'Namn är obligatoriskt';
    if (!f.org_nummer.trim())         e.org_nummer    = 'Organisationsnummer är obligatoriskt';
    if (!f.adress.gata.trim())        e.gata          = 'Gata är obligatorisk';
    if (!f.adress.postnummer.trim())  e.postnummer    = 'Postnummer är obligatoriskt';
    if (!f.adress.ort.trim())         e.ort           = 'Ort är obligatorisk';
    if (!f.kontaktperson.trim())      e.kontaktperson = 'Kontaktperson är obligatorisk';
    if (!f.epost.trim())              e.epost         = 'E-post är obligatorisk';
    if (!f.telefon.trim())            e.telefon       = 'Telefon är obligatorisk';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setSub(true);
    try {
      const payload = { ...form, customer_id: user?.customer_id || 'default' };
      if (isEdit) {
        await api.put(`/clients/${clientId}`, payload);
        toast.success('Kund uppdaterad!');
      } else {
        await api.post('/clients', payload);
        toast.success('Kund skapad!');
      }
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Åtgärden misslyckades');
    } finally {
      setSub(false);
    }
  };

  const fieldErr = (key) => submitted && errors[key];

  return (
    <Card
      className="border-2 animate-in fade-in-0 slide-in-from-top-2 duration-200"
      style={{ borderColor: '#F9CF3F' }}
      data-testid="client-inline-form"
    >
      <CardContent className="pt-5 pb-6 px-6">
        <form onSubmit={handleSubmit} noValidate data-testid="client-form">

          {/* ══ Grundläggande information ══ */}
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">
            Grundläggande information
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div className="space-y-1.5">
              <Label htmlFor="cl-namn">
                Namn <span className="text-destructive" aria-hidden="true">*</span>
              </Label>
              <Input
                id="cl-namn"
                data-testid="client-namn-input"
                value={form.namn}
                onChange={e => set('namn', e.target.value)}
                aria-required="true"
                aria-invalid={!!fieldErr('namn')}
                className={fieldErr('namn') ? 'border-destructive focus-visible:ring-destructive' : ''}
                autoFocus
              />
              {fieldErr('namn') && (
                <p role="alert" className="text-xs text-destructive">{errors.namn}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cl-org-nummer">
                Organisationsnummer <span className="text-destructive" aria-hidden="true">*</span>
              </Label>
              <Input
                id="cl-org-nummer"
                data-testid="client-org-nummer-input"
                value={form.org_nummer}
                onChange={e => set('org_nummer', e.target.value)}
                aria-required="true"
                aria-invalid={!!fieldErr('org_nummer')}
                className={fieldErr('org_nummer') ? 'border-destructive focus-visible:ring-destructive' : ''}
              />
              {fieldErr('org_nummer') && (
                <p role="alert" className="text-xs text-destructive">{errors.org_nummer}</p>
              )}
            </div>
          </div>

          {/* ══ Adress ══ */}
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4 mt-2">
            Adress
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="sm:col-span-2 space-y-1.5">
              <Label htmlFor="cl-gata">
                Gata <span className="text-destructive" aria-hidden="true">*</span>
              </Label>
              <Input
                id="cl-gata"
                data-testid="client-gata-input"
                value={form.adress.gata}
                onChange={e => setNested('adress', 'gata', e.target.value)}
                aria-required="true"
                aria-invalid={!!fieldErr('gata')}
                className={fieldErr('gata') ? 'border-destructive focus-visible:ring-destructive' : ''}
              />
              {fieldErr('gata') && (
                <p role="alert" className="text-xs text-destructive">{errors.gata}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cl-postnummer">
                Postnummer <span className="text-destructive" aria-hidden="true">*</span>
              </Label>
              <Input
                id="cl-postnummer"
                data-testid="client-postnummer-input"
                value={form.adress.postnummer}
                onChange={e => setNested('adress', 'postnummer', e.target.value)}
                aria-required="true"
                aria-invalid={!!fieldErr('postnummer')}
                className={fieldErr('postnummer') ? 'border-destructive focus-visible:ring-destructive' : ''}
              />
              {fieldErr('postnummer') && (
                <p role="alert" className="text-xs text-destructive">{errors.postnummer}</p>
              )}
            </div>
            <div className="sm:col-span-3 space-y-1.5">
              <Label htmlFor="cl-ort">
                Ort <span className="text-destructive" aria-hidden="true">*</span>
              </Label>
              <Input
                id="cl-ort"
                data-testid="client-ort-input"
                value={form.adress.ort}
                onChange={e => setNested('adress', 'ort', e.target.value)}
                aria-required="true"
                aria-invalid={!!fieldErr('ort')}
                className={fieldErr('ort') ? 'border-destructive focus-visible:ring-destructive' : ''}
              />
              {fieldErr('ort') && (
                <p role="alert" className="text-xs text-destructive">{errors.ort}</p>
              )}
            </div>
          </div>

          {/* ══ Kontaktinformation ══ */}
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4 mt-2">
            Kontaktinformation
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="space-y-1.5">
              <Label htmlFor="cl-kontaktperson">
                Kontaktperson <span className="text-destructive" aria-hidden="true">*</span>
              </Label>
              <Input
                id="cl-kontaktperson"
                data-testid="client-kontaktperson-input"
                value={form.kontaktperson}
                onChange={e => set('kontaktperson', e.target.value)}
                aria-required="true"
                aria-invalid={!!fieldErr('kontaktperson')}
                className={fieldErr('kontaktperson') ? 'border-destructive focus-visible:ring-destructive' : ''}
              />
              {fieldErr('kontaktperson') && (
                <p role="alert" className="text-xs text-destructive">{errors.kontaktperson}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cl-epost">
                E-post <span className="text-destructive" aria-hidden="true">*</span>
              </Label>
              <Input
                id="cl-epost"
                type="email"
                data-testid="client-epost-input"
                value={form.epost}
                onChange={e => set('epost', e.target.value)}
                aria-required="true"
                aria-invalid={!!fieldErr('epost')}
                className={fieldErr('epost') ? 'border-destructive focus-visible:ring-destructive' : ''}
              />
              {fieldErr('epost') && (
                <p role="alert" className="text-xs text-destructive">{errors.epost}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cl-telefon">
                Telefon <span className="text-destructive" aria-hidden="true">*</span>
              </Label>
              <Input
                id="cl-telefon"
                data-testid="client-telefon-input"
                value={form.telefon}
                onChange={e => set('telefon', e.target.value)}
                aria-required="true"
                aria-invalid={!!fieldErr('telefon')}
                className={fieldErr('telefon') ? 'border-destructive focus-visible:ring-destructive' : ''}
              />
              {fieldErr('telefon') && (
                <p role="alert" className="text-xs text-destructive">{errors.telefon}</p>
              )}
            </div>
          </div>

          {/* ══ Kategori ══ */}
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4 mt-2">
            Kategori
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div className="space-y-1.5">
              <Label htmlFor="cl-typ">Typ</Label>
              <select
                id="cl-typ"
                data-testid="client-typ-select"
                value={form.kategori.typ}
                onChange={e => setNested('kategori', 'typ', e.target.value)}
                className={selectCls(false)}
              >
                {KATEGORI_TYPER.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cl-underkategori">Underkategori</Label>
              <Input
                id="cl-underkategori"
                data-testid="client-underkategori-input"
                value={form.kategori.underkategori || ''}
                onChange={e => setNested('kategori', 'underkategori', e.target.value)}
              />
            </div>
          </div>

          {/* ══ Verksamhetsuppdrag ══ */}
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4 mt-2">
            Verksamhetsuppdrag
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {VERKSAMHETSUPPDRAG_OPTIONS.map(option => {
              const checked = form.verksamhetsuppdrag.includes(option);
              return (
                <label
                  key={option}
                  className={`flex items-center gap-2 text-sm cursor-pointer rounded-md px-3 py-2 border transition-colors ${
                    checked
                      ? 'border-[#F9CF3F] bg-[#F9CF3F]/10 font-medium'
                      : 'border-input hover:border-input/80'
                  }`}
                  data-testid={`uppdrag-${option.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <input
                    type="checkbox"
                    className="rounded border-input w-4 h-4 accent-[#F9CF3F]"
                    checked={checked}
                    onChange={() => toggleUppdrag(option)}
                  />
                  <span className="leading-tight">{option}</span>
                </label>
              );
            })}
          </div>

          {/* ══ Övrig information ══ */}
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4 mt-2">
            Övrig information
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div className="space-y-1.5">
              <Label htmlFor="cl-kollektivavtal">Kollektivavtal</Label>
              <Input
                id="cl-kollektivavtal"
                data-testid="client-kollektivavtal-input"
                value={form.kollektivavtal}
                onChange={e => set('kollektivavtal', e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cl-antal-anvandare">Antal användare</Label>
              <Input
                id="cl-antal-anvandare"
                data-testid="client-antal-anvandare-input"
                type="number"
                min={1}
                value={form.antal_anvandare}
                onChange={e => set('antal_anvandare', parseInt(e.target.value) || 1)}
              />
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label htmlFor="cl-fakturaunderlag">Fakturaunderlag</Label>
              <Textarea
                id="cl-fakturaunderlag"
                data-testid="client-fakturaunderlag-input"
                value={form.fakturaunderlag}
                onChange={e => set('fakturaunderlag', e.target.value)}
                rows={3}
              />
            </div>
          </div>

          {/* ══ Betalningsinformation ══ */}
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4 mt-2">
            Betalningsinformation
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="space-y-1.5">
              <Label htmlFor="cl-bankgiro">Bankgiro</Label>
              <Input
                id="cl-bankgiro"
                data-testid="client-bankgiro-input"
                value={form.betalning.bankgiro || ''}
                onChange={e => setNested('betalning', 'bankgiro', e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cl-plusgiro">Plusgiro</Label>
              <Input
                id="cl-plusgiro"
                data-testid="client-plusgiro-input"
                value={form.betalning.plusgiro || ''}
                onChange={e => setNested('betalning', 'plusgiro', e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cl-bankkonto">Bankkonto</Label>
              <Input
                id="cl-bankkonto"
                data-testid="client-bankkonto-input"
                value={form.betalning.bankkonto || ''}
                onChange={e => setNested('betalning', 'bankkonto', e.target.value)}
              />
            </div>
          </div>

          {/* ══ Status ══ */}
          <div className="space-y-1.5 mb-6">
            <Label htmlFor="cl-status">Status</Label>
            <select
              id="cl-status"
              data-testid="client-status-select"
              value={form.status}
              onChange={e => set('status', e.target.value)}
              className={selectCls(false)}
              style={{ maxWidth: '200px' }}
            >
              {STATUS_OPTIONS.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          {/* ══ Knappar ══ */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="ghost"
              onClick={onCancel}
              disabled={submitting}
              className="rounded-full"
            >
              Avbryt
            </Button>
            <Button
              type="submit"
              data-testid="client-submit-btn"
              disabled={submitting}
              className="rounded-full font-bold px-8"
              style={{ background: '#F9CF3F', color: '#333333' }}
            >
              {submitting ? 'Sparar...' : isEdit ? 'Uppdatera kund' : 'Skapa kund'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

// ── ClientsPage ────────────────────────────────────────────────────────────────

const ClientsPage = () => {
  const { user }                      = useAuth();
  const [clients, setClients]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [formOpen, setFormOpen]       = useState(false);
  const [editClient, setEditClient]   = useState(null);

  // Kunder är ENBART super_admin
  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => { fetchClients(); }, []);

  const fetchClients = async () => {
    try {
      const res = await api.get('/clients');
      setClients(res.data);
    } catch {
      toast.error('Kunde inte hämta kunder');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm('Är du säker på att du vill radera denna kund?')) return;
    try {
      await api.delete(`/clients/${clientId}`);
      toast.success('Kund raderad!');
      fetchClients();
    } catch {
      toast.error('Kunde inte radera kund');
    }
  };

  const openCreate = () => { setEditClient(null); setFormOpen(true); };
  const openEdit   = (client) => { setEditClient(client); setFormOpen(true); };
  const closeForm  = () => { setFormOpen(false); setEditClient(null); };

  const handleSaved = async () => {
    closeForm();
    await fetchClients();
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="clients-page">

        {/* ── Header ── */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-heading font-black tracking-tight flex items-center gap-3">
              <Building2 className="text-blue-600" size={36} />
              Kunder
            </h1>
            <p className="text-muted-foreground mt-2">Hantera kundorganisationer</p>
          </div>
          {isSuperAdmin && (
            formOpen ? (
              <Button variant="ghost" className="rounded-full" onClick={closeForm}>
                <X size={16} className="mr-1.5" /> Avbryt
              </Button>
            ) : (
              <Button
                data-testid="create-client-btn"
                className="rounded-full font-bold"
                onClick={openCreate}
              >
                <Plus size={18} className="mr-2" /> Ny kund
              </Button>
            )
          )}
        </div>

        {/* ── Inline-formulär ── */}
        {isSuperAdmin && formOpen && (
          <ClientForm
            key={editClient?.id ?? 'new'}
            initial={editClient ? fromClient(editClient) : buildEmpty()}
            onSave={handleSaved}
            onCancel={closeForm}
            isEdit={!!editClient}
            clientId={editClient?.id}
          />
        )}

        {/* ── Kundlista ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            <Card className="col-span-full">
              <CardContent className="p-6 text-muted-foreground">Laddar kunder...</CardContent>
            </Card>
          ) : clients.length === 0 ? (
            <Card className="col-span-full">
              <CardContent className="p-12 text-center">
                <Building2 size={64} className="mx-auto mb-4 text-muted-foreground/30" />
                <p className="text-muted-foreground text-lg">Inga kunder ännu.</p>
                {isSuperAdmin && !formOpen && (
                  <Button className="mt-4 rounded-full font-bold" onClick={openCreate}>
                    <Plus size={16} className="mr-2" /> Skapa första kunden
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            clients.map((client) => (
              <Card
                key={client.id}
                data-testid={`client-card-${client.id}`}
                className="hover:shadow-lg transition-shadow"
              >
                <CardHeader className="border-b pb-4">
                  <CardTitle className="font-heading font-bold text-lg flex items-center gap-2">
                    <Building2 size={24} className="text-blue-600 shrink-0" />
                    {client.namn}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4 space-y-3">
                  <div className="space-y-2 text-sm">
                    <p><strong>Org.nr:</strong> {client.org_nummer}</p>
                    <div className="flex items-center gap-2">
                      <Mail size={16} className="text-muted-foreground shrink-0" />
                      <span className="truncate">{client.epost}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone size={16} className="text-muted-foreground shrink-0" />
                      <span>{client.telefon}</span>
                    </div>
                    <p className="text-muted-foreground text-xs">
                      {client.adress?.gata}, {client.adress?.postnummer} {client.adress?.ort}
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-2 mt-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                      client.kategori?.typ === 'uppdragsgivare'
                        ? 'bg-blue-100 text-blue-600'
                        : 'bg-primary/20 text-foreground'
                    }`}>
                      {client.kategori?.typ}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                      client.status === 'aktiv'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      {client.status}
                    </span>
                  </div>

                  {client.verksamhetsuppdrag?.length > 0 && (
                    <div className="pt-3 border-t">
                      <p className="text-xs font-bold text-muted-foreground mb-2">Verksamhetsuppdrag:</p>
                      <div className="flex flex-wrap gap-1">
                        {client.verksamhetsuppdrag.slice(0, 3).map(v => (
                          <span key={v} className="text-xs bg-muted px-2 py-1 rounded">{v}</span>
                        ))}
                        {client.verksamhetsuppdrag.length > 3 && (
                          <span className="text-xs text-muted-foreground">
                            +{client.verksamhetsuppdrag.length - 3} till
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {isSuperAdmin && (
                    <div className="flex gap-2 pt-3 border-t">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEdit(client)}
                        className="flex-1 rounded-full"
                        data-testid={`edit-client-${client.id}`}
                      >
                        <Edit size={14} className="mr-1" /> Redigera
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(client.id)}
                        className="rounded-full px-3"
                        data-testid={`delete-client-${client.id}`}
                      >
                        <Trash2 size={14} className="text-destructive" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export { ClientForm, buildEmpty, fromClient };
export default ClientsPage;
